import { ChangeDetectorRef, Component, HostBinding, Input, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { MenuService } from '../../../services/layout/app.menu.servie';
import { FavoritesService } from '../../../services/favorites.service';

interface MenuBadgeViewItem {
	count: number;
	backgroundColor: string;
	textColor: string;
	tooltip?: string;
}

@Component({
	selector: '[app-menuitem]',
	styleUrls: ['./app-menuitem.component.scss'],
	template: `
		<ng-container>
			<div *ngIf="root && item.visible !== false" class="layout-menuitem-root-text">{{ item.label }}</div>
			<a
				*ngIf="(!item.routerLink || item.items) && item.visible !== false"
				[attr.href]="item.url"
				(click)="itemClick($event)"
				[ngClass]="item.class"
				[attr.target]="item.target"
				tabindex="0"
				pRipple
			>
				<i [ngClass]="item.icon" class="layout-menuitem-icon"></i>
				<span class="layout-menuitem-text">{{ item.label }}</span>
				<ng-container *ngFor="let badge of getBadgeItems()">
					<span
						class="layout-menuitem-badge"
						[ngStyle]="getBadgeStyle(badge)"
						[pTooltip]="badge.tooltip || undefined"
						tooltipPosition="top"
						tooltipStyleClass="menu-badge-tooltip"
					>
						{{ getBadgeText(badge.count) }}
					</span>
				</ng-container>
				<i class="pi pi-fw pi-angle-down layout-submenu-toggler" *ngIf="item.items"></i>
			</a>

			<a
				*ngIf="item.routerLink && !item.items && item.visible !== false"
				(click)="itemClick($event)"
				[ngClass]="item.class"
				[routerLink]="item.routerLink"
				routerLinkActive="active-route"
				[routerLinkActiveOptions]="
					item.routerLinkActiveOptions || {
						paths: 'exact',
						queryParams: 'ignored',
						matrixParams: 'ignored',
						fragment: 'ignored'
					}
				"
				[fragment]="item.fragment"
				[queryParamsHandling]="item.queryParamsHandling"
				[preserveFragment]="item.preserveFragment"
				[skipLocationChange]="item.skipLocationChange"
				[replaceUrl]="item.replaceUrl"
				[state]="item.state"
				[queryParams]="item.queryParams"
				[attr.target]="item.target"
				tabindex="0"
				pRipple
			>
				<i [ngClass]="item.icon" class="layout-menuitem-icon"></i>
				<span class="layout-menuitem-text">{{ item.label }}</span>
				<ng-container *ngFor="let badge of getBadgeItems()">
					<span
						class="layout-menuitem-badge"
						[ngStyle]="getBadgeStyle(badge)"
						[pTooltip]="badge.tooltip || undefined"
						tooltipPosition="top"
						tooltipStyleClass="menu-badge-tooltip"
					>
						{{ getBadgeText(badge.count) }}
					</span>
				</ng-container>
				<i class="pi pi-fw pi-angle-down layout-submenu-toggler" *ngIf="item.items"></i>
				<i
					*ngIf="isFavoritable()"
					class="layout-menuitem-favorite"
					[ngClass]="isFavorite() ? 'pi pi-star-fill' : 'pi pi-star'"
					[class.is-favorite]="isFavorite()"
					(click)="toggleFavorite($event)"
					[pTooltip]="isFavorite() ? 'Remove from My Favorites' : 'Add to My Favorites'"
					tooltipPosition="top"
				></i>
			</a>

			<ul *ngIf="item.items && item.visible !== false" [@children]="submenuAnimation">
				<ng-template ngFor let-child let-i="index" [ngForOf]="item.items" [ngForTrackBy]="trackByMenuItem">
					<li app-menuitem [item]="child" [index]="i" [parentKey]="key" [class]="child.badgeClass"></li>
				</ng-template>
			</ul>
		</ng-container>
	`,
	animations: [
		trigger('children', [
			state(
				'collapsed',
				style({
					height: '0'
				})
			),
			state(
				'expanded',
				style({
					height: '*'
				})
			),
			transition('collapsed <=> expanded', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'))
		])
	]
})
export class AppMenuitemComponent implements OnInit, OnDestroy {
	@Input() item: any;

	@Input() index!: number;

	@Input() @HostBinding('class.layout-root-menuitem') root!: boolean;

	@Input() parentKey!: string;

	active = false;

	menuSourceSubscription: Subscription;

	menuResetSubscription: Subscription;

	favoritesSubscription: Subscription;

	key: string = '';

	constructor(
		private cd: ChangeDetectorRef,
		public router: Router,
		private menuService: MenuService,
		private favoritesService: FavoritesService
	) {
		this.favoritesSubscription = this.favoritesService.favoriteKeys$.subscribe(() => {
			// Favorite state changed elsewhere (e.g. this or a sibling item's star
			// was toggled) - re-check isFavorite() for this item on next CD cycle.
			this.cd.markForCheck();
		});

		this.menuSourceSubscription = this.menuService.menuSource$.subscribe((value) => {
			Promise.resolve(null).then(() => {
				if (value.routeEvent) {
					this.active = value.key === this.key || value.key.startsWith(this.key + '-') ? true : false;
				} else {
					if (value.key !== this.key && !value.key.startsWith(this.key + '-')) {
						this.active = false;
					}
				}
			});
		});

		this.menuResetSubscription = this.menuService.resetSource$.subscribe(() => {
			this.active = false;
		});

		this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((params) => {
			if (this.item.routerLink) {
				this.updateActiveStateFromRoute();
			}
		});
	}

	ngOnInit() {
		this.key = this.parentKey ? this.parentKey + '-' + this.index : String(this.index);

		if (this.item.routerLink) {
			this.updateActiveStateFromRoute();
		}
	}

	updateActiveStateFromRoute() {
		// Items mirrored into "My Favorites" are duplicates of an item that also
		// lives in its own group. Broadcasting route-active state from the copy
		// would deactivate every branch that doesn't match the copy's key -
		// collapsing the group the canonical item sits in. Let only the canonical
		// item drive auto-expansion; the copy still highlights via routerLinkActive.
		if (this.item?.favoriteCopy) {
			return;
		}

		let activeRoute = this.router.isActive(this.item.routerLink[0], {
			paths: 'exact',
			queryParams: 'ignored',
			matrixParams: 'ignored',
			fragment: 'ignored'
		});

		if (activeRoute) {
			this.menuService.onMenuStateChange({ key: this.key, routeEvent: true });
		}
	}

	itemClick(event: Event) {
		// avoid processing disabled items
		if (this.item.disabled) {
			event.preventDefault();
			return;
		}

		// execute command
		if (this.item.command) {
			this.item.command({ originalEvent: event, item: this.item });
		}

		// toggle active state
		if (this.item.items) {
			this.active = !this.active;
		}

		this.menuService.onMenuStateChange({ key: this.key });

		if (this.item.items && this.active && this.item.onExpand) {
			Promise.resolve().then(() => {
				this.item.onExpand();
			});
		}
	}

	get submenuAnimation() {
		return this.root ? 'expanded' : this.active ? 'expanded' : 'collapsed';
	}

	/**
	 * Which badges to render — gated on the count alone.
	 *
	 * These guards used to also pass when badgeLoading was true, which meant
	 * every item grew a badge the moment a fetch started; anything that came
	 * back 0 then lost it again, and that repeated on each open since expanding
	 * Inbound Processing refetches. badgeLoading no longer belongs here at all
	 * now that the badge renders its count directly rather than a spinner —
	 * including it would just show a "0" for the duration of the request and
	 * then remove it.
	 *
	 * Counts aren't cleared between fetches, so a refresh leaves the previous
	 * number in place until the new one arrives.
	 */
	/**
	 * Which badges to render — gated on the count alone.
	 *
	 * These guards used to also pass when badgeLoading was true, which meant
	 * every item grew a badge the moment a fetch started; anything that came
	 * back 0 then lost it again, and that repeated on each open since expanding
	 * Inbound Processing refetches. badgeLoading no longer belongs here at all
	 * now that the badge renders its count directly rather than a spinner —
	 * including it would just show a "0" for the duration of the request and
	 * then remove it.
	 *
	 * Counts aren't cleared between fetches, so a refresh leaves the previous
	 * number in place until the new one arrives.
	 */
	getBadgeItems(): MenuBadgeViewItem[] {
		if (this.root) return [];

		if (Array.isArray(this.item?.badgeItems) && this.item.badgeItems.length > 0) {
			return this.item.badgeItems.filter((b: MenuBadgeViewItem) => b.count > 0);
		}

		if (Number.isInteger(this.item?.badgeCount) && this.item.badgeCount > 0) {
			return [
				{
					count: this.item.badgeCount,
					backgroundColor: 'var(--badge-work-queue-bg)',
					textColor: 'var(--badge-work-queue-text)'
				}
			];
		}

		return [];
	}

	getBadgeStyle(badge: MenuBadgeViewItem) {
		return {
			opacity: '1',
			filter: 'none',
			'box-shadow': 'none',
			'background-color': badge.backgroundColor,
			color: badge.textColor
		};
	}

	getBadgeText(count: number): string {
		return count > 99 ? '99+' : String(count);
	}

	/**
	 * Without trackBy, every favorites (or badge-count) update replaces the
	 * entire menu tree with freshly-spread objects, so Angular's default
	 * reference-based diffing tears down and recreates every menuitem in the
	 * sidebar - collapsing/re-expanding whatever group happens to be open.
	 * Tracking by each item's stable identity (routerLink for leaf items,
	 * label for groups) lets Angular reuse existing component instances
	 * instead, so unrelated branches of the tree are left alone.
	 */
	trackByMenuItem(index: number, item: any): string {
		if (item?.routerLink) {
			return (item.routerLink as string[]).join('/');
		}
		return item?.label ?? String(index);
	}

	/**
	 * Stable key for a leaf menu item, matching how the backend identifies a
	 * favorited menu item (its routerLink segments joined with '/').
	 * Only leaf items have a routerLink, so this is undefined for groups.
	 */
	private menuItemKey(): string | undefined {
		return this.item?.routerLink ? (this.item.routerLink as string[]).join('/') : undefined;
	}

	/**
	 * Only leaf/sub-menu items (items with a routerLink) are favoritable.
	 * Top-level groups never render a star.
	 *
	 * Also gated on favorites having loaded: until then every star would render
	 * unfilled regardless of the item's real state, so clicking one could send a
	 * toggle that removes an existing favorite the user meant to keep.
	 */
	isFavoritable(): boolean {
		return (
			!this.root && this.item?.favoritable !== false && !!this.menuItemKey() && this.favoritesService.isLoaded
		);
	}

	isFavorite(): boolean {
		return this.favoritesService.isFavorite(this.menuItemKey());
	}

	toggleFavorite(event: Event): void {
		event.stopPropagation();
		event.preventDefault();

		const key = this.menuItemKey();
		if (key) {
			this.favoritesService.toggleFavorite(key, this.item?.label);
		}
	}

	@HostBinding('class.active-menuitem')
	get activeClass() {
		return this.active && !this.root;
	}

	ngOnDestroy() {
		if (this.menuSourceSubscription) {
			this.menuSourceSubscription.unsubscribe();
		}

		if (this.menuResetSubscription) {
			this.menuResetSubscription.unsubscribe();
		}

		if (this.favoritesSubscription) {
			this.favoritesSubscription.unsubscribe();
		}
	}
}
